import { useRateFieldError } from "../../../../hooks/useRateFieldError";
import { FC, MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Box, Stack } from "@mui/material";
import { FormType, PrimaryMainColor, getField } from "@simplyk/common";
import { useFormContext, useWatch } from "react-hook-form";

import { useRateContext } from "../../../../Context/RateContext";
import { useRateFieldName } from "../../../../hooks/useRateFieldName";
import { ReceiptEligibleAmountField } from "../../../../RateFields/ReceiptEligibleAmountField/ReceiptEligibleAmountField";
import { useIsExtrasSectionOpen } from "../../../ExtrasSection/ExtrasSection";
import { RateOptionToggleRow } from "../../RateOptionToggleRow";

import { useShowTaxReceiptRow } from "./useShowTaxReceiptRow";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { useCurrentUserContext } from "@/common/contexts/CurrentUserContext";
import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";
import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import { useTranslate } from "@/common/hooks/useTranslate";
import { ReceiptPDFPreview } from "@/components/PDFPreview/ReceiptPDFPreview";
import { PdfPreviewModal } from "@/components/PdfPreviewModal/PdfPreviewModal";
import { PreviewButton } from "@/components/PreviewButton/PreviewButton";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { useStableLiveFormEditorContext } from "@/features/LiveFormEditor/common/LiveFormEditorContext";
import { StepTicketsFormData } from "@/features/LiveFormEditor/Steps/StepTickets/types";

const stopPropagation = (event: MouseEvent) => event.stopPropagation();

interface TaxReceiptRowProps {
  tooltip?: string;
}

export const TaxReceiptRow: FC<TaxReceiptRowProps> = ({ tooltip: tooltipProp }) => {
  const { t } = useTranslate();
  const { locale } = useLocaleContext();
  const { organization } = useCurrentUserContext();
  const { logAmplitudeEvent } = useAmplitude();
  const { isSmallScreen } = useMediaQuery();
  const { formCategory, isEditing, isOnboarding, isRegistration } = useRateContext();
  const { control, setValue, clearErrors } = useFormContext<StepTicketsFormData>();
  const { apiFormObject: apiTicketing } = useStableLiveFormEditorContext<"ticketing">();
  const isExtrasOpen = useIsExtrasSectionOpen();
  const showRow = useShowTaxReceiptRow();

  const generateReceiptField = useRateFieldName("isGeneratingReceipt");
  const eligibleAmountField = useRateFieldName("receiptEligibleAmount");
  const isPayWhatYouCan = useWatch({ control, name: useRateFieldName("isPayWhatYouCan") });
  const isChecked = useWatch({ control, name: generateReceiptField });
  const eligibleAmount = useWatch({ control, name: eligibleAmountField }) ?? undefined;
  const amount = useWatch({ control, name: useRateFieldName("amount") });
  const rateName = useWatch({ control, name: useRateFieldName("name") });

  const eligibleAmountError = useRateFieldError("receiptEligibleAmount");

  const isReceiptOn = Boolean(isChecked) && !isPayWhatYouCan;

  const wasGeneratingReceiptRef = useRef(Boolean(isChecked));
  const didJustEnableReceipt = Boolean(isChecked) && !wasGeneratingReceiptRef.current;
  useEffect(() => {
    wasGeneratingReceiptRef.current = Boolean(isChecked);
  }, [isChecked]);

  const [pdfModalOpen, setPdfModalOpen] = useState(false);

  const ticketingField = getField(apiTicketing.ticketingFields, apiTicketing.locale);

  const handleToggle = useCallback(
    (checked: boolean) => {
      setValue(generateReceiptField, checked, { shouldDirty: true });
      logAmplitudeEvent(AmplitudeEvents.TicketOptionToggled, {
        option: "tax_receipt",
        enabled: checked,
        entry_point: "manual",
        form_type: formCategory,
        is_liveform: true,
        isEditing,
        isOnboarding,
      });
      if (!checked) {
        setValue(eligibleAmountField, null);
        clearErrors(eligibleAmountField);
      }
    },
    [
      setValue,
      generateReceiptField,
      logAmplitudeEvent,
      formCategory,
      isEditing,
      isOnboarding,
      eligibleAmountField,
      clearErrors,
    ]
  );

  const previewParams = useMemo(
    () => ({
      locale,
      amount,
      eligibleAmount,
      rateName,
      ticketingName: ticketingField?.title || "",
      formType: FormType.Ticketing,
      formColor: apiTicketing.color || organization?.color || PrimaryMainColor,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pdfModalOpen, amount, eligibleAmount, rateName]
  );

  if (!showRow) {
    return null;
  }

  return (
    <>
      <RateOptionToggleRow
        optionKey="taxReceipt"
        checked={isReceiptOn}
        onToggle={handleToggle}
        disabled={isPayWhatYouCan}
        disabledTooltip={t("dashboard", "ticketing.payWhatYouCanDisabledExistingReceipts")}
        label={t("dashboard", "ticketing.rateOption.issueTaxReceipt")}
        tooltip={
          tooltipProp ??
          t(
            "dashboard",
            isRegistration
              ? "ticketing.rateEligibleAmountInformations.registration"
              : "ticketing.rateEligibleAmountInformations"
          )
        }
        inlineConfig={
          isReceiptOn ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <DashboardTypography
                component="span"
                variant="caption"
                vibe="text-neutral-quiet"
                sx={{ whiteSpace: "nowrap" }}
              >
                {t("dashboard", "ticketing.rateOption.eligibleAmount")}
              </DashboardTypography>
              <ReceiptEligibleAmountField isJustChecked={didJustEnableReceipt} toggleRateOption={isExtrasOpen} />
              {!isSmallScreen && (
                <Box component="span" onClick={stopPropagation} sx={{ display: "inline-flex" }}>
                  <PreviewButton handlePreviewClick={async () => setPdfModalOpen(true)} size="small" />
                </Box>
              )}
            </Stack>
          ) : undefined
        }
        belowContent={
          isReceiptOn && eligibleAmountError?.message ? (
            <Box component="span">
              <DashboardTypography component="p" variant="caption" vibe="error-main">
                {eligibleAmountError.message}
              </DashboardTypography>
            </Box>
          ) : undefined
        }
      />

      {isReceiptOn && !isSmallScreen && (
        <PdfPreviewModal onClose={() => setPdfModalOpen(false)} open={pdfModalOpen}>
          <ReceiptPDFPreview {...previewParams} />
        </PdfPreviewModal>
      )}
    </>
  );
};
